import React, { useState, useEffect } from 'react';
import { supabase } from '../services/supabaseClient';
import { deleteAccount } from '../services/accountApi';
import { redeemCoupon, issueCoupon, listCoupons } from '../services/couponApi';
import NoticeBoard from './NoticeBoard';

const APP_VERSION = '1.0.0';
const DELETE_CONFIRM_WORD = '削除';

export default function InfoScreen({ onBack, onNav, onLogout, onSiblings, isGuest, isAdmin, onTeacher, onStudent }) {
  const [showNotices, setShowNotices] = useState(false);
  const [googleLinked, setGoogleLinked] = useState(null); // null=確認中 / true / false
  const [linking, setLinking] = useState(false);
  const [linkError, setLinkError] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  // クーポンコード適用
  const [couponCode, setCouponCode] = useState('');
  const [redeeming, setRedeeming] = useState(false);
  const [couponError, setCouponError] = useState('');
  const [couponResult, setCouponResult] = useState(null);

  // クーポンコード発行（管理者のみ）
  const [issueCode, setIssueCode] = useState('');
  const [issueDays, setIssueDays] = useState('');
  const [issueUses, setIssueUses] = useState('1');
  const [issueNote, setIssueNote] = useState('');
  const [issuing, setIssuing] = useState(false);
  const [issueError, setIssueError] = useState('');
  const [issueResult, setIssueResult] = useState(null);
  const [issuedCoupons, setIssuedCoupons] = useState(null); // null=未取得
  const [couponsListError, setCouponsListError] = useState('');

  const refreshCoupons = () => {
    if (!isAdmin) return;
    setCouponsListError('');
    listCoupons()
      .then(setIssuedCoupons)
      .catch((e) => {
        setIssuedCoupons(null);
        setCouponsListError(e.message || '発行履歴の取得に失敗しました');
      });
  };

  useEffect(() => {
    refreshCoupons();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  const handleRedeemCoupon = async () => {
    if (!couponCode.trim()) return;
    setCouponError('');
    setCouponResult(null);
    setRedeeming(true);
    try {
      const result = await redeemCoupon(couponCode.trim());
      setCouponResult(result);
      setCouponCode('');
    } catch (e) {
      setCouponError(e.message || '適用に失敗しました');
    } finally {
      setRedeeming(false);
    }
  };

  const handleIssueCoupon = async () => {
    setIssueError('');
    setIssueResult(null);
    setIssuing(true);
    try {
      const result = await issueCoupon({
        code: issueCode.trim(),
        extendDays: issueDays,
        maxUses: issueUses,
        note: issueNote.trim() || undefined,
      });
      setIssueResult(result);
      setIssueCode('');
      setIssueDays('');
      setIssueUses('1');
      setIssueNote('');
      refreshCoupons();
    } catch (e) {
      setIssueError(e.message || '発行に失敗しました');
    } finally {
      setIssuing(false);
    }
  };

  useEffect(() => {
    if (isGuest) return;
    let cancelled = false;
    supabase.auth.getUserIdentities().then(({ data, error }) => {
      if (cancelled) return;
      if (error) { setGoogleLinked(false); return; }
      setGoogleLinked((data?.identities || []).some(i => i.provider === 'google'));
    });
    return () => { cancelled = true; };
  }, [isGuest]);

  const handleLinkGoogle = async () => {
    setLinkError('');
    setLinking(true);
    const { error } = await supabase.auth.linkIdentity({
      provider: 'google',
      options: { redirectTo: `${window.location.origin}/app` },
    });
    // 成功時はGoogleへリダイレクトされるのでここには戻ってこない。
    if (error) {
      setLinking(false);
      setLinkError('連携を開始できませんでした：' + error.message);
    }
  };

  const handleDeleteAccount = async () => {
    setDeleteError('');
    setDeleting(true);
    try {
      await deleteAccount();
      setShowDeleteConfirm(false);
      onLogout && onLogout();
    } catch (e) {
      setDeleting(false);
      setDeleteError(e.message || '削除に失敗しました');
    }
  };

  return (
    <div className="screen info-screen">

      <button className="btn-back" onClick={onBack}>← もどる</button>

      <h2 className="info-screen-title">設定</h2>

      {/* メインメニュー（お知らせ・メッセージ） */}
      <div className="info-menu">

        <button
          className="info-menu-item"
          onClick={() => setShowNotices(true)}
        >
          <span className="info-menu-icon">📢</span>
          <span className="info-menu-label">お知らせ</span>
          <span className="info-menu-arrow">›</span>
        </button>

        <button
          className="info-menu-item"
          onClick={() => onNav && onNav('feedback')}
        >
          <span className="info-menu-icon">📬</span>
          <span className="info-menu-label">メッセージを送る</span>
          <span className="info-menu-sub">ご意見・ご要望</span>
          <span className="info-menu-arrow">›</span>
        </button>

        {onSiblings && (
          <button className="info-menu-item" onClick={onSiblings}>
            <span className="info-menu-icon">👨‍👧‍👦</span>
            <span className="info-menu-label">きょうだいの管理</span>
            <span className="info-menu-sub">プロフィールの追加・編集</span>
            <span className="info-menu-arrow">›</span>
          </button>
        )}

        {/* 生徒ページはlocalStorage完結なのでゲストでも使える */}
        {onStudent && (
          <button className="info-menu-item" onClick={onStudent}>
            <span className="info-menu-icon">🎒</span>
            <span className="info-menu-label">生徒ページ</span>
            <span className="info-menu-sub">先生からの課題・じぶんのきろく</span>
            <span className="info-menu-arrow">›</span>
          </button>
        )}

        {/* ゲストにも出す。開いた先でログインを案内する（機能を知ってもらう機会にする） */}
        {onTeacher && (
          <button className="info-menu-item" onClick={onTeacher}>
            <span className="info-menu-icon">🏫</span>
            <span className="info-menu-label">先生ページ</span>
            <span className="info-menu-sub">
              クラスに音読課題を配る{isGuest ? '（ログインが必要）' : ''}
            </span>
            <span className="info-menu-arrow">›</span>
          </button>
        )}

        {/* ゲストは連携できるアカウントがないので出さない */}
        {!isGuest && googleLinked === false && (
          <button className="info-menu-item" onClick={handleLinkGoogle} disabled={linking}>
            <span className="info-menu-icon">🔗</span>
            <span className="info-menu-label">{linking ? '連携画面を開いています…' : 'Googleでもログインできるようにする'}</span>
            <span className="info-menu-sub">今のアカウントにGoogleログインを追加</span>
            <span className="info-menu-arrow">›</span>
          </button>
        )}
        {!isGuest && googleLinked === true && (
          <div className="info-menu-item" style={{ opacity: 0.7, cursor: 'default' }}>
            <span className="info-menu-icon">✅</span>
            <span className="info-menu-label">Googleと連携済み</span>
          </div>
        )}
        {linkError && <div className="auth-error">{linkError}</div>}

      </div>

      {/* クーポンコード（体験期間の延長など。ゲストはコードを紐づけるアカウントが無いので出さない） */}
      {!isGuest && (
        <div className="info-menu info-coupon-card">
          <div className="info-coupon-title">🎟 クーポンコード</div>
          <div className="info-coupon-row">
            <input
              className="auth-input info-coupon-input"
              value={couponCode}
              onChange={(e) => setCouponCode(e.target.value)}
              placeholder="コードを入力"
              disabled={redeeming}
            />
            <button
              className="info-coupon-btn"
              onClick={handleRedeemCoupon}
              disabled={redeeming || !couponCode.trim()}
            >
              {redeeming ? '適用中…' : '適用する'}
            </button>
          </div>
          {couponError && <div className="auth-error">{couponError}</div>}
          {couponResult && (
            <div className="info-coupon-success">
              先生ページの体験期間が{couponResult.extendDays}日延長されました
              {couponResult.newEndsAt && `（〜${new Date(couponResult.newEndsAt).toLocaleDateString('ja-JP')}）`}
            </div>
          )}
        </div>
      )}

      {/* クーポン発行（管理者専用） */}
      {isAdmin && (
        <div className="info-menu info-coupon-card">
          <div className="info-coupon-title">🎟 クーポン発行（管理者用）</div>
          <div className="info-coupon-sub">先生ページの体験期間を延長するコードを発行します（現状これ以外の用途はありません）</div>
          <div className="info-coupon-issue-grid">
            <input
              className="auth-input"
              value={issueCode}
              onChange={(e) => setIssueCode(e.target.value)}
              placeholder="コード（例: SENSEI2026）"
              disabled={issuing}
            />
            <input
              className="auth-input"
              type="number"
              min="1"
              value={issueDays}
              onChange={(e) => setIssueDays(e.target.value)}
              placeholder="延長日数"
              disabled={issuing}
            />
            <div className="info-coupon-field">
              <input
                className="auth-input"
                type="number"
                min="1"
                value={issueUses}
                onChange={(e) => setIssueUses(e.target.value)}
                placeholder="使用回数上限"
                disabled={issuing}
              />
              <div className="info-coupon-field-label">使用回数（何人まで使えるか）</div>
            </div>
            <input
              className="auth-input"
              value={issueNote}
              onChange={(e) => setIssueNote(e.target.value)}
              placeholder="メモ（例: ○○先生用）"
              disabled={issuing}
            />
          </div>
          <button
            className="info-coupon-btn"
            onClick={handleIssueCoupon}
            disabled={issuing || !issueCode.trim() || !issueDays || !issueUses}
          >
            {issuing ? '発行中…' : '発行する'}
          </button>
          {issueError && <div className="auth-error">{issueError}</div>}
          {issueResult && (
            <div className="info-coupon-success">
              発行しました：{issueResult.code}（{issueResult.extendDays}日延長・
              {issueResult.maxUses}回まで）
            </div>
          )}

          {/* 発行履歴 */}
          <div className="info-coupon-history-title">発行履歴</div>
          {couponsListError && (
            <div className="auth-error">{couponsListError}</div>
          )}
          {issuedCoupons === null && !couponsListError && (
            <div className="info-coupon-field-label">読み込み中…</div>
          )}
          {issuedCoupons?.length === 0 && (
            <div className="info-coupon-field-label">まだ発行したコードはありません</div>
          )}
          {issuedCoupons?.length > 0 && (
            <div className="info-coupon-history-list">
              {issuedCoupons.map((c) => (
                <div key={c.code} className="info-coupon-history-row">
                  <div className="info-coupon-history-code">
                    {c.code}
                    {!c.enabled && <span className="info-coupon-history-disabled">無効</span>}
                  </div>
                  <div className="info-coupon-history-detail">
                    {c.extend_days}日延長・使用 {c.used_count}/{c.max_uses}
                    {c.note ? `・${c.note}` : ''}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ログアウト */}
      {onLogout && (
        <button className="info-logout-btn" onClick={onLogout}>
          ログイン画面にもどる
        </button>
      )}

      {/* アカウント削除（ゲストは削除するアカウントがないので出さない） */}
      {!isGuest && (
        <button
          className="info-logout-btn"
          style={{ color: '#FCA5A5', marginTop: 8 }}
          onClick={() => { setDeleteError(''); setDeleteConfirmText(''); setShowDeleteConfirm(true); }}
        >
          アカウントを削除する
        </button>
      )}

      {/* スペーサー */}
      <div style={{ flex: 1 }} />

      {/* 法的リンク＋バージョン（下部） */}
      <div className="info-legal-area">
        <a className="info-legal-link" href="/privacy.html" target="_blank" rel="noopener noreferrer">
          プライバシーポリシー
        </a>
        <span className="info-legal-sep">・</span>
        <a className="info-legal-link" href="/terms.html" target="_blank" rel="noopener noreferrer">
          利用規約
        </a>
        <span className="info-legal-sep">・</span>
        <a className="info-legal-link" href="/tokushoho.html" target="_blank" rel="noopener noreferrer">
          特定商取引法に基づく表記
        </a>
        <div className="info-version">バージョン {APP_VERSION}</div>
      </div>


      {showNotices && (
        <NoticeBoard onClose={() => setShowNotices(false)} showAll={true} />
      )}

      {/* アカウント削除確認モーダル */}
      {showDeleteConfirm && (
        <div className="sibling-delete-overlay" onClick={() => !deleting && setShowDeleteConfirm(false)}>
          <div className="sibling-delete-modal" onClick={e => e.stopPropagation()}>
            <div className="sibling-delete-icon">⚠️</div>
            <div className="sibling-delete-name">アカウントを削除しますか？</div>
            <p className="sibling-delete-msg">
              きょうだい全員のプロフィール・きろく・音声データがすべて削除されます。<br />
              <span className="sibling-delete-warn">この操作は取り消せません</span>
            </p>
            <p className="sibling-delete-msg" style={{ fontSize: 13 }}>
              よろしければ「{DELETE_CONFIRM_WORD}」と入力してください
            </p>
            <input
              className="auth-input"
              value={deleteConfirmText}
              onChange={(e) => setDeleteConfirmText(e.target.value)}
              placeholder={DELETE_CONFIRM_WORD}
              disabled={deleting}
              style={{ marginBottom: 12, textAlign: 'center' }}
            />
            {deleteError && <div className="auth-error">{deleteError}</div>}
            <div className="sibling-delete-btns">
              <button className="sibling-delete-cancel" onClick={() => setShowDeleteConfirm(false)} disabled={deleting}>
                キャンセル
              </button>
              <button
                className="sibling-delete-confirm"
                onClick={handleDeleteAccount}
                disabled={deleting || deleteConfirmText !== DELETE_CONFIRM_WORD}
              >
                {deleting ? '削除中…' : '削除する'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
